import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  MapPinned,
  UserRoundCog,
  HandCoins,
  Store,
  Users,
  CircleCheck,
} from "lucide-react";
import {
  ProfileCard,
  CandidatDrawer,
  EnseigneDrawer,
  VilleDrawer,
  LocationDrawer,
} from "../../profile_utils";
import { Spinner } from "./spinner";
import { useMatchingProfiles } from "./hooks";
import { useCampaign } from "./campaign-context";

interface ProfilesListProps {
  entityType: "candidats" | "enseignes" | "villes" | "locations";
  filters: Array<{ id: string; type: string }>;
  hasSelection: boolean;
}

interface SortAttribute {
  level: number;
  name?: string;
  label: string;
  value?: string;
  source?: {
    id: number;
    type: "candidat" | "enseigne" | "ville" | "location";
    name: string;
  };
}

interface MissingAttribute {
  level: number;
  label: string;
}

// Memoized component: only re-renders if filters or hasSelection change
export const ProfilesList = React.memo(
  function ProfilesList({
    entityType,
    filters,
    hasSelection,
  }: ProfilesListProps) {
    const [page, setPage] = useState(1);

    const {
      selectProfile,
      deselectProfile,
      isProfileSelected,
      getSelectedForType,
      setSelectedEntity,
      mainProfile,
    } = useCampaign();

    // Check if the source is the current user's main profile
    const isMainProfileSource = (source: SortAttribute["source"]) => {
      if (!source) return false;
      return source.id === mainProfile.id && source.type === mainProfile.type;
    };

    // Get tooltip text based on whether source is main profile or selection
    const getTooltipText = (source: SortAttribute["source"]) => {
      if (!source) return "";
      if (isMainProfileSource(source)) {
        return source.name;
      }
      return `Parce que vous avez sélectionné : ${source.name}`;
    };

    // Use the filters passed as props - React Query will cache based on these exact filters
    // CRITICAL: Disable query if block has a selection, but React Query will keep the last data
    const { data: response, isLoading } = useMatchingProfiles(
      entityType,
      page,
      filters,
      !hasSelection, // enabled: only fetch if NO selection
    );

    const selectedForThisType = getSelectedForType(entityType);

    const data = response?.[entityType] || [];
    const pagination = response?.pagination || null;
    const matchingCriteria = response?.matching_criteria || null;
    const missingFields = response?.missing_fields || [];
    const sortAttributes: SortAttribute[] = response?.sort_attributes || [];
    const missingAttributes: MissingAttribute[] =
      response?.missing_attributes || [];

    // Get icon for attribute category
    const getAttributeIcon = (attrName?: string) => {
      switch (attrName) {
        case "geoloc_match":
          return <MapPinned size={14} />;
        case "secteur_match":
          return <UserRoundCog size={14} />;
        case "contribution_match":
          return <HandCoins size={14} />;
        case "surface_match":
          return <Store size={14} />;
        case "chalandise_match":
          return <Users size={14} />;
        default:
          return null;
      }
    };

    // Format display value based on attribute type
    const formatAttrValue = (attr: SortAttribute) => {
      if (attr.name === "geoloc_match" && attr.value) {
        return `À proximité de ${attr.value}`;
      }
      if (attr.name === "contribution_match" && attr.value) {
        return `Ok avec ${attr.value}`;
      }
      if (attr.name === "chalandise_match" && attr.value) {
        return `Ok avec ${attr.value}`;
      }
      return attr.value;
    };

    const getTitle = () => {
      switch (entityType) {
        case "candidats":
          return "Sélectionnez un entrepreneur";
        case "enseignes":
          return "Sélectionnez une enseigne";
        case "villes":
          return "Sélectionnez une collectivité";
        case "locations":
          return "Sélectionnez un emplacement";
      }
    };

    const getButtonLabel = () => {
      switch (entityType) {
        case "candidats":
          return "Voir tous les entrepreneurs";
        case "enseignes":
          return "Voir toutes les enseignes";
        case "villes":
          return "Voir toutes les villes";
        case "locations":
          return "Voir tous les emplacements";
      }
    };

    const getButtonHref = () => {
      switch (entityType) {
        case "candidats":
          return "/search/candidats";
        case "enseignes":
          return "/search/enseignes";
        case "villes":
          return "/search/collectivites";
        case "locations":
          return "/search/emplacements";
      }
    };

    const handlePrevPage = () => {
      if (pagination && pagination.current_page > 1) {
        setPage(pagination.current_page - 1);
      }
    };

    const handleNextPage = () => {
      if (pagination && pagination.current_page < pagination.total_pages) {
        setPage(pagination.current_page + 1);
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="px-4 py-2 border-b border-slate-400/60 h-[51px]">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-lg inline-flex items-center gap-1.5">
              {getTitle()}
              <ChevronDown size={18} className="text-slate-400" />
            </h3>
            <div>
              {pagination && pagination.total_pages > 1 && (
                <nav
                  className="inline-flex items-center divide divide-x divide-slate-300 border shadow rounded-lg bg-white"
                  role="pagination"
                  aria-label="pager"
                >
                  <span className="prev">
                    <button
                      onClick={handlePrevPage}
                      disabled={pagination.current_page === 1 || hasSelection}
                      className={`w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-l-lg ${
                        pagination.current_page === 1 || hasSelection
                          ? "pointer-events-none opacity-40"
                          : ""
                      }`}
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={20} />
                    </button>
                  </span>
                  <span className="next">
                    <button
                      onClick={handleNextPage}
                      disabled={
                        pagination.current_page === pagination.total_pages ||
                        hasSelection
                      }
                      className={`w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-r-lg ${
                        pagination.current_page === pagination.total_pages ||
                        hasSelection
                          ? "pointer-events-none opacity-40"
                          : ""
                      }`}
                      aria-label="Next page"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </span>
                </nav>
              )}
            </div>
          </div>
        </div>
        {sortAttributes.length > 0 && (
          <div className="px-4 py-2 border-b border-slate-400/60 bg-slate-50 text-xs">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {sortAttributes.map((attr, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-default-100 text-default-700 rounded-full border border-default-200 group relative cursor-pointer"
                    title={getTooltipText(attr.source)}
                  >
                    {getAttributeIcon(attr.name)}
                    {attr.value && <span>{formatAttrValue(attr)}</span>}
                    <sup className="text-green-600 -mr-0.5">
                      <CircleCheck size={10} />
                    </sup>
                    {/* Tooltip */}
                    {attr.source && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-10">
                        {getTooltipText(attr.source)}
                      </span>
                    )}
                  </span>
                ))}
              </div>
              {missingAttributes.length > 0 && (
                <div className="relative group">
                  <div className="flex items-center gap-1.5 text-orange-600 cursor-help">
                    <AlertCircle size={16} />
                    <span className="text-xs font-medium">Tri incomplet</span>
                  </div>
                  <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                    <p className="text-xs text-slate-600 mb-2">
                      Complétez votre profil pour affiner le tri :
                    </p>
                    <ul className="space-y-1">
                      {missingAttributes.map((attr, index) => (
                        <li
                          key={index}
                          className="text-xs text-orange-600 flex items-start gap-1.5"
                        >
                          <span className="text-orange-400 mt-0.5">•</span>
                          <span>{attr.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {missingFields.length > 0 && (
          <div className="px-4 py-2 border-b border-slate-400/60 bg-orange-50 text-xs text-orange-600">
            Compléter vos informations pour affiner : {missingFields.join(", ")}
          </div>
        )}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <div className="-mt-4">
                <Spinner size="lg" variant="default" />
              </div>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center text-slate-500">
              <p className="font-medium">Aucun match</p>
              <p className="text-sm">Complétez votre profil</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {data.map((entity) => {
                const isSelected = isProfileSelected(entity.id, entityType);
                const isDisabled = hasSelection && !isSelected;

                return (
                  <ProfileCard
                    key={entity.id}
                    entity={entity}
                    entityType={entityType}
                    sortAttributes={sortAttributes}
                    onClick={() => {
                      if (isSelected) {
                        // Clicking on selected item deselects it
                        deselectProfile(entity.id, entityType);
                      } else {
                        // Select this profile
                        selectProfile({
                          id: entity.id,
                          type: entityType,
                          entity: entity,
                        });
                      }
                    }}
                    onDetailClick={() => setSelectedEntity(entity)}
                    selected={isSelected}
                    disabled={!!isDisabled}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if filters or hasSelection actually changed
    if (prevProps.hasSelection !== nextProps.hasSelection) {
      return false; // Props changed, re-render
    }

    // Deep comparison of filters array
    if (prevProps.filters.length !== nextProps.filters.length) {
      return false; // Props changed, re-render
    }

    // Compare each filter object
    for (let i = 0; i < prevProps.filters.length; i++) {
      if (
        prevProps.filters[i].id !== nextProps.filters[i].id ||
        prevProps.filters[i].type !== nextProps.filters[i].type
      ) {
        return false; // Props changed, re-render
      }
    }

    // Props are the same, skip re-render
    return true;
  },
);
